import { useEffect, useRef } from 'react';

interface UseModalArgs {
  handlers: Partial<{ onClose: VoidFunction; onOpen: VoidFunction }>;
  preventScrollJump: boolean;
  stopInsideFocus: boolean;
}

let lockedScrollPosition: number | null = null;

const lockBodyScroll = () => {
  if (lockedScrollPosition !== null) return;

  lockedScrollPosition = window.scrollY;
  document.body.style.setProperty('--scroll-y', `${lockedScrollPosition}px`);
  document.body.style.position = 'fixed';
  document.body.style.top = `-${lockedScrollPosition}px`;
};

const unlockBodyScroll = () => {
  if (lockedScrollPosition === null) return;

  document.body.style.removeProperty('--scroll-y');
  document.body.style.position = '';
  document.body.style.top = '';
  window.scrollTo(0, lockedScrollPosition);
  lockedScrollPosition = null;
};

const closeOpenDialogs = () => {
  Array.from(document.querySelectorAll<HTMLDialogElement>('dialog[open]'))
    .reverse()
    .forEach((dialog) => dialog.close());
};

const useModal = ({
  handlers,
  stopInsideFocus,
  preventScrollJump = true,
}: Partial<UseModalArgs> | undefined = {}) => {
  const ref = useRef<HTMLDialogElement>(null);
  const interactionStartedInsideModal = useRef(false);

  const openModal = () => {
    const dialog = ref.current;
    if (!dialog || dialog.open) return;

    dialog.showModal();

    if (preventScrollJump) {
      lockBodyScroll();
    }

    handlers?.onOpen?.();

    if (stopInsideFocus) {
      requestAnimationFrame(() => {
        if (ref.current) {
          ref.current.focus();
        }
      });
    }
  };

  const closeModal = () => {
    ref.current?.close();
  };

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    const handleCancel = (event: Event) => {
      event.preventDefault();
      closeOpenDialogs();
    };

    const handleClose = () => {
      unlockBodyScroll();
      handlers?.onClose?.();
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('close', handleClose);

    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('close', handleClose);
    };
  });

  const trackInteractionStart = (
    e:
      React.MouseEvent<HTMLDialogElement> | React.TouchEvent<HTMLDialogElement>,
  ) => {
    interactionStartedInsideModal.current = e.target !== ref.current;
  };

  const onClick = (e: React.SyntheticEvent<HTMLDialogElement>) => {
    const clickIsOnMask =
      !interactionStartedInsideModal.current && e.target === ref.current;

    const clickIsOnClosableElement =
      (e.target as HTMLElement).getAttribute('data-close-modal') !== null;

    if (clickIsOnMask || clickIsOnClosableElement) {
      closeModal();
    }
  };

  return {
    ref,
    openModal,
    closeModal,
    maskClickHandlers: {
      onMouseDown: trackInteractionStart,
      onTouchStart: trackInteractionStart,
      onClick,
    },
  };
};

export default useModal;
